import React, { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { Stack, Box, Typography } from "@mui/material";
import PasteInput from "./UI/PasteInput";
import MapImage from "assets/images/map.png";
import { MouseHoverPopover } from "Reuse/UI";
import { StyledFormHelperTextCustom } from "customStyled";
import { logger } from "utils";
import { useFieldName } from "./rhf/FieldPath";
import { markTouched } from "./rhf/useAppForm";
import { useAppFormContext } from "./rhf/FormProvider";
import { useTranslation } from "react-i18next";
import { splitCoordinatePair } from "utils/coordinates";

// Paste from the clipboard: a pair ("30.208, -97.744" or
// 30°12'29.1"N 97°44'39.0"W) fills both fields, a single value this one
const pasteCoordinates = (event, status, setData) => {
  event.preventDefault();
  navigator.clipboard
    .readText()
    .then((text) => {
      if (!text?.trim()) return;
      const pair = splitCoordinatePair(text);
      if (pair) {
        setData("latitude", pair[0]);
        setData("longitude", pair[1]);
      } else {
        setData(status, text);
      }
    })
    .catch((err) => logger.error("Could not read clipboard text:", err));
};

// Messages for the coordinate rules (utils/coordinates)
export const coordinateErrors = (t, axis) => {
  const suffix = axis === "lat" ? "Lat" : "Lng";
  return [
    { validation: "coordinateFormat", errorMsg: t(`coordinateFormat${suffix}`) },
    { validation: "coordinateMissingDirection", errorMsg: t(`coordinateMissingDirection${suffix}`) },
    { validation: "coordinateDirection", errorMsg: t(`coordinateDirection${suffix}`) },
    { validation: "coordinateMinutes", errorMsg: t("coordinateMinutes") },
    { validation: "coordinateRange", errorMsg: t(`coordinateRange${suffix}`) },
  ];
};

// Latitude / longitude, resolved through the current FieldPath ("address")
const LongitudeLatitudeInput = () => {
  const form = useAppFormContext();
  const names = {
    latitude: useFieldName("latitude"),
    longitude: useFieldName("longitude"),
  };
  const latitude = useWatch({ name: names.latitude });
  const longitude = useWatch({ name: names.longitude });
  const prev = useRef({ latitude, longitude });

  // Like before: any value change marks that field touched (errors show while typing)
  useEffect(() => {
    ["latitude", "longitude"].forEach((key) => {
      const value = key === "latitude" ? latitude : longitude;
      if (value !== prev.current[key]) markTouched(form, names[key]);
    });
    prev.current = { latitude, longitude };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  const handlePaste = (event, status) =>
    pasteCoordinates(event, status, (field, text) => {
      form.setValue(names[field], text.trim(), {
        shouldValidate: true,
        shouldTouch: true,
      });
      // a paste is a user edit: Save must send it (setValue alone keeps the
      // form "pristine", so the save was skipped without a message)
      form.markUserChanged?.(names[field]);
    });

  return <LongitudeLatitudeUI handlePaste={handlePaste} />;
};

const LongitudeLatitudeUI = ({ handlePaste }) => {
  const { t } = useTranslation("form");
  return (
    <Stack direction="column" spacing={2} sx={{ flexGrow: 1 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          // top-aligned: an error message under one field must not move the other
          alignItems: "flex-start",
          flexGrow: 1
        }}>
        <PasteInput
          id="latitude"
          label={t("latitude")}
          placeholder={t("enterLatitude")}
          autoComplete="latitude"
          required
          handlePaste={handlePaste}
          attr={{ maxLength: 30 }}
          errorMsg={coordinateErrors(t, "lat")}
          aria-describedby="longitude-latitude-helper-text"
        />
        <PasteInput
          id="longitude"
          label={t("longitude")}
          placeholder={t("enterLongitude")}
          autoComplete="longitude"
          required
          handlePaste={handlePaste}
          attr={{ maxLength: 30 }}
          errorMsg={coordinateErrors(t, "lng")}
          aria-describedby="longitude-latitude-helper-text"
        />
      </Stack>
      <StyledFormHelperTextCustom id="longitude-latitude-helper-text">
        {t("weCollectLatitudeLongitudeEvery")}
        <MouseHoverPopover icon>
          <Stack sx={{ padding: "8px 12px" }} spacing={1}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {t("howCopyLatitudeLongitude")}
            </Typography>
            <Box sx={{ width: 240 }}>
              <img src={MapImage} alt="longitude-latitude" width="100%" />
            </Box>
          </Stack>
        </MouseHoverPopover>
      </StyledFormHelperTextCustom>
    </Stack>
  );
};

export default LongitudeLatitudeInput;
